import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button, SvgIcon } from "@mui/material";

import "./style.scss";
import { ReactComponent as UploadIcon } from "../../../assets/cloudUploadIcon.svg";
import { ReactComponent as TrashIcon } from "../../../assets/trash.icon.svg";
import { ReactComponent as RefreshIcon } from "../../../assets/refreshIcon.svg";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { trialFileTypes } from "../../../common/utilities/constants/constants";
import {
    fetchProviders,
    fetchContentFolders,
    fetchLearningObject,
    fetchLearningObjects,
    deleteLearningObjects
} from "../../../api/admin/content-providers/endpoints-referrers";
import { publishRecognitionResult } from "../../../api/admin/recognition-result/endpoints-referrers";
import { SortType } from "../../../common/enums/sort-type.enum";
import {
    TrialLearningObjectStatus,
    LearningObjectStatus
} from "../../../common/enums/learning-object-status.enum";
import { ProviderType } from "../../../common/enums/provider-type.enum";
import { Provider } from "../../../common/interfaces/admin/provider.interface";
import { LearningObject } from "../../../common/interfaces/admin/learning-object.interface";
import { RecognitionResult } from "../../../common/interfaces/admin/recognition-result.interface";
import { MenuItem } from "../../../common/interfaces/comps/menu-item.interface";
import { LoaderView } from "../../../components/loader/loader.comp";
import {
    CheckboxMenuFilterT,
    CheckboxMenuFilter,
    SelectionFilter,
    ActionMenu
} from "../../../components/menu-filter/menu-filter.comp";
import HeaderLayout from "../../../components/layout/header-layout.comp";
import TrialLearningObjects from "../../../components/admin/trial-learning-objects/trial-learning-objects.comp";
import FileUploadView from "../file-upload/file-upload";
import ConfirmationPopup, {
    ConfirmationPopupType
} from "../../../components/confirmation-popup/confirmation-popup.comp";
import { ModalTemplate } from "../../../components/modal-template/modal-template.comp";
import FeedbackView from "../feedback/feedback";

const TrialKnowledgeBaseView = () => {
    const DEFAULT_PAGE_INDEX = 1;
    const DEFAULT_PAGE_SIZE = 50;

    const navigate = useNavigate();
    const { user } = FirebaseAuthContainer.useContainer();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [providerId, setProviderId] = useState<number>();
    const [courseId, setCourseId] = useState<string>();
    const [learningObjects, setLearningObjects] = useState<LearningObject[]>([]);
    const [checkedLOs, setCheckedLOs] = useState<LearningObject[]>([]);
    const [deletableUids, setDeletableUids] = useState<string[]>([]);
    const [checkedTypes, setCheckedTypes] = useState<MenuItem<string>[]>([]);
    const [checkedStatuses, setCheckedStatuses] = useState<string[]>([]);
    const [selectedSort, setSelectedSort] = useState<string>(SortType.newestFirst);
    const [sortList] = useState<string[]>(Object.values(SortType));
    const [types] = useState<MenuItem<string>[]>(trialFileTypes);
    const [statuses] = useState<string[]>(Object.values(TrialLearningObjectStatus));
    const [totalCount, setTotalCount] = useState<number>(0);
    const [pageIndex, setPageIndex] = useState<number>(DEFAULT_PAGE_INDEX);
    const [editableRR, setEditableRR] = useState<RecognitionResult>();
    const [publishSubtitle, setPublishSubtitle] = useState<string>();
    const [feedbackOpen, setFeedbackOpen] = useState<boolean>(false);
    const [filesUploadOpen, setFilesUploadOpen] = useState<boolean>(false);
    const [publishConfirmationOpen, setPublishConfirmationOpen] = useState<boolean>(false);
    const [unpublishConfirmationOpen, setUnpublishConfirmationOpen] = useState<boolean>(false);
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState<boolean>(false);
    const publishDescription = `When you publish one version of the document,
    all other versions will be unpublished. Knowledge Base will use published
    version for answering questions regarding this document.`;
    const unpublishDescription = `When you unpublish all documents, Knowledge Base will no longer be
    able to answer questions regarding this document.`;

    useEffect(() => {
        if (!user) return;

        setIsLoading(true);
        fetchProviders(user.companyId).then(response => {
            const provider = response.data.find(
                (item: Provider) => item.name === ProviderType.fileUploader
            );
            if (provider) {
                setProviderId(provider.id);
            } else {
                setIsLoading(false);
            }
        });
    }, [user]);

    useEffect(() => {
        if (!user || !providerId) return;

        fetchContentFolders(user.companyId, providerId).then(response => {
            if (response.data.length && response.data[0].courses.length) {
                setCourseId(response.data[0].courses[0].courseId);
            } else {
                setIsLoading(false);
            }
        });
    }, [user, providerId]);

    useEffect(() => {
        if (user && courseId) {
            loadLearningObjects(
                user.companyId,
                courseId,
                createQuery(
                    DEFAULT_PAGE_INDEX,
                    DEFAULT_PAGE_SIZE,
                    checkedTypes,
                    checkedStatuses,
                    selectedSort
                )
            );
        } else {
            setLearningObjects([]);
        }
    }, [user, courseId, checkedTypes, checkedStatuses, selectedSort]);

    const createQuery = (
        page: number,
        pageSize: number,
        checkedTypes: MenuItem<string>[],
        checkedStatuses: string[],
        selectedSort: string
    ) => {
        const queryParts: string[] = [];

        queryParts.push(`page=${page}`);
        queryParts.push(`pageSize=${pageSize}`);

        if (checkedTypes.length > 0) {
            queryParts.push(`sourceType=${checkedTypes.map(type => type.value).join(",")}`);
        }

        if (checkedStatuses.length > 0) {
            queryParts.push(
                `status=${checkedStatuses
                    .map(status => {
                        switch (status) {
                            case TrialLearningObjectStatus.ready:
                                return `${LearningObjectStatus.inUse},${LearningObjectStatus.active}`;
                            case TrialLearningObjectStatus.idle:
                                return `${LearningObjectStatus.idle}`;
                            case TrialLearningObjectStatus.waiting:
                                return `${LearningObjectStatus.recognizing},${LearningObjectStatus.transcribing},${LearningObjectStatus.waitingForDownload},${LearningObjectStatus.waitingForRecognition}`;
                            case TrialLearningObjectStatus.error:
                                return `${LearningObjectStatus.error}`;
                        }
                    })
                    .join(",")}`
            );
        }

        const sortMap = {
            [SortType.newestFirst]: "sortBy=createdAt&sortOrder=desc",
            [SortType.oldestFirst]: "sortBy=createdAt&sortOrder=asc",
            [SortType.az]: "sortBy=name&sortOrder=asc",
            [SortType.za]: "sortBy=name&sortOrder=desc"
        };

        if (selectedSort && sortMap[selectedSort as SortType]) {
            queryParts.push(sortMap[selectedSort as SortType]);
        }

        return `?${queryParts.join("&")}`;
    };

    const loadLearningObjects = (companyId: number, courseId: string, query?: string) => {
        fetchLearningObjects(companyId, courseId, query).then(response => {
            const {
                data: { count, items }
            } = response;
            setTotalCount(count);
            setLearningObjects(items);
            setPageIndex(DEFAULT_PAGE_INDEX);
            setIsLoading(false);
        });
    };

    const appendLearningObjects = (companyId: number, courseId: string, query?: string) => {
        fetchLearningObjects(companyId, courseId, query).then(response => {
            const {
                data: { count, items }
            } = response;
            setTotalCount(count);
            setLearningObjects(prevItems => [...prevItems, ...items]);
            setPageIndex(prevIndex => prevIndex + 1);
        });
    };

    const handleAppendLearningObjects = useCallback(() => {
        if (!user || !courseId) return;

        appendLearningObjects(
            user.companyId,
            courseId,
            createQuery(
                pageIndex + 1,
                DEFAULT_PAGE_SIZE,
                checkedTypes,
                checkedStatuses,
                selectedSort
            )
        );
    }, [user, courseId, pageIndex, checkedTypes, checkedStatuses, selectedSort]);

    const handleChangePublishStatus = (rr: RecognitionResult, loName: string) => {
        setEditableRR(rr);
        setPublishSubtitle(loName);
        rr.isPublish ? setUnpublishConfirmationOpen(true) : setPublishConfirmationOpen(true);
    };

    const handlePublishConfirmationAction = () => {
        if (!user || !courseId || !editableRR) return;

        publishRecognitionResult(user.companyId, editableRR.uid, true).then(() => {
            fetchLearningObject(user.companyId, courseId, editableRR.loItemId).then(response => {
                const currentLO = response.data;
                setLearningObjects(loList =>
                    loList.map(lo => (lo.loItemId === editableRR.loItemId ? currentLO : lo))
                );
            });
        });
        setPublishConfirmationOpen(false);
    };

    const handleUnpublishConfirmationAction = () => {
        if (!user || !courseId || !editableRR) return;

        publishRecognitionResult(user.companyId, editableRR.uid, false).then(() => {
            fetchLearningObject(user.companyId, courseId, editableRR.loItemId).then(response => {
                const currentLO = response.data;
                setLearningObjects(loList =>
                    loList.map(lo => (lo.loItemId === editableRR.loItemId ? currentLO : lo))
                );
            });
        });
        setUnpublishConfirmationOpen(false);
    };

    const handleDeleteCheckedLearningObjects = () => {
        const uids = checkedLOs.map((item: LearningObject) => item.loItemId);
        if (uids.length > 0) {
            handleDeleteLearningObjects(uids);
        }
    };

    const handleDeleteLearningObjects = (uids: string[]) => {
        setDeletableUids(uids);
        setDeleteConfirmationOpen(true);
    };

    const handleDeleteConfirmationAction = () => {
        if (!user || !courseId || !deletableUids.length) return;

        deleteLearningObjects(user.companyId, courseId, deletableUids).then(() => {
            setLearningObjects(prevLOs =>
                prevLOs.filter(lo => !deletableUids.find(uid => uid === lo.loItemId))
            );
            setTotalCount(prevValue => prevValue - deletableUids.length);
            setCheckedLOs([]);
        });

        setDeleteConfirmationOpen(false);
    };

    const viewRecognitionResult = (uid: string) => {
        navigate(`/recognition-result/${providerId}/${uid}`);
    };

    const handleFilesUpload = () => {
        setFilesUploadOpen(true);
    };

    const handleFilesUploadClose = (needUpdate: boolean) => {
        setFilesUploadOpen(false);

        if (needUpdate && user && courseId) {
            loadLearningObjects(
                user.companyId,
                courseId,
                createQuery(
                    DEFAULT_PAGE_INDEX,
                    DEFAULT_PAGE_SIZE,
                    checkedTypes,
                    checkedStatuses,
                    selectedSort
                )
            );
        }
    };

    const handleFeedbackOpen = () => {
        setFeedbackOpen(true);
    };

    const handleFeedbackClose = () => {
        setFeedbackOpen(false);
    };

    return (
        <HeaderLayout blurHeader={false}>
            <div className="uploaded-files-view">
                <div className="uploaded-files-title">
                    <span className="uploaded-files-title__text">My Files</span>
                </div>
                <div className="uploaded-files-filters">
                    <div className="file-table-filter file-table-filter--actions">
                        <ActionMenu
                            label="Action"
                            disabled={checkedLOs.length === 0}
                            actions={[
                                { name: "Delete", action: handleDeleteCheckedLearningObjects }
                            ]}
                            minWidth={160}
                        />
                    </div>
                    <div className="file-table-filter-group">
                        <div className="file-table-filter file-table-filter--types">
                            <CheckboxMenuFilterT
                                label="Type"
                                list={types}
                                checkedList={checkedTypes}
                                setCheckedList={setCheckedTypes}
                                showCounter={true}
                                rightPosition={true}
                                minWidth={180}
                            />
                        </div>
                        <div className="file-table-filter file-table-filter--status">
                            <CheckboxMenuFilter
                                label="Status"
                                list={statuses}
                                checkedList={checkedStatuses}
                                setCheckedList={setCheckedStatuses}
                                rightPosition={true}
                                minWidth={180}
                            />
                        </div>
                        <div className="file-table-filter file-table-filter--sorting">
                            <SelectionFilter
                                list={sortList}
                                selectedItem={selectedSort}
                                setSelectedItem={setSelectedSort}
                                rightPosition={true}
                                minWidth={180}
                            />
                        </div>
                    </div>
                    <Button
                        className="file-table-upload-button"
                        variant="text"
                        startIcon={
                            <SvgIcon
                                className="file-table-upload-button__icon"
                                component={UploadIcon}
                                inheritViewBox
                            />
                        }
                        onClick={handleFilesUpload}
                    >
                        Upload File(s)
                    </Button>
                </div>
                <div className="uploaded-files-table">
                    <TrialLearningObjects
                        totalCount={totalCount}
                        learningObjects={learningObjects}
                        checkedLOs={checkedLOs}
                        setCheckedLOs={setCheckedLOs}
                        handleAppendLOs={handleAppendLearningObjects}
                        handleDelete={handleDeleteLearningObjects}
                        handleAIResult={viewRecognitionResult}
                        handleChangePublishStatus={handleChangePublishStatus}
                    />
                    <LoaderView show={isLoading} />
                </div>
                {/* <div className="uploaded-files-footer">
                    {!assistantAvailable && (
                        <span className="uploaded-files-footer-message">
                            It may take from 5 min. up to an hour for AI to process your files.
                        </span>
                    )}
                    <Button
                        className="uploaded-files-footer-button"
                        variant="text"
                        disabled={!assistantAvailable}
                        onClick={handleTryKnowledgeBase}
                    >
                        Try your Knowledge Base
                    </Button>
                </div> */}
                <ModalTemplate
                    open={feedbackOpen}
                    width={654}
                    height={497}
                    handleClose={handleFeedbackClose}
                >
                    <FeedbackView handleClose={handleFeedbackClose} />
                </ModalTemplate>
                <ModalTemplate
                    open={filesUploadOpen}
                    width={658}
                    height={466}
                    handleClose={handleFilesUploadClose}
                >
                    <FileUploadView
                        handleClose={handleFilesUploadClose}
                        handleFeedback={handleFeedbackOpen}
                    />
                </ModalTemplate>
                <ConfirmationPopup
                    open={deleteConfirmationOpen}
                    type={ConfirmationPopupType.dangerous}
                    icon={TrashIcon}
                    title="Delete"
                    description={`You are trying to delete ${deletableUids.length} file(s).
                            This can't be undone!`}
                    actionName="Delete"
                    handleAction={handleDeleteConfirmationAction}
                    handleClose={() => setDeleteConfirmationOpen(false)}
                />
                <ConfirmationPopup
                    open={publishConfirmationOpen}
                    icon={RefreshIcon}
                    title="Publish"
                    subtitle={publishSubtitle}
                    description={publishDescription}
                    actionName="Publish"
                    showCancelButton={false}
                    handleAction={handlePublishConfirmationAction}
                    handleClose={() => setPublishConfirmationOpen(false)}
                />
                <ConfirmationPopup
                    open={unpublishConfirmationOpen}
                    icon={RefreshIcon}
                    title="Unpublish"
                    subtitle={publishSubtitle}
                    description={unpublishDescription}
                    actionName="Unpublish"
                    showCancelButton={false}
                    handleAction={handleUnpublishConfirmationAction}
                    handleClose={() => setUnpublishConfirmationOpen(false)}
                />
            </div>
        </HeaderLayout>
    );
};

export default TrialKnowledgeBaseView;
